import { DOCUMENT } from '@angular/common';
import { tenantVocabulary } from './app/terminology/tenant-message';
import {
    afterRenderEffect,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    inject,
    viewChild,
    effect,
    untracked
} from '@angular/core';

import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';
import translationsIT from '../public/i18n/it-IT.json';
import { TranslateService } from '@ngx-translate/core';
import { PrimeNG } from 'primeng/config';
import { tap } from 'rxjs';
import { AnalyticsService } from './app/services/analytics.service';
import { SeoService } from './app/services/seo.service';
import { getStaticPageSeo } from './app/content/public-content.catalog';
import { CompassWorkspaceHostComponent } from './app/components/shared/compass-workspace-host.component';

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [RouterModule, CompassWorkspaceHostComponent],
    template: `
        <router-outlet></router-outlet>
        @defer (on idle) {
            <app-compass-workspace-host></app-compass-workspace-host>
        }

        <!-- Cookie banner -->
        @if (showCookieBanner) {
            <div
                #cookieBanner
                class="app-cookie-banner fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:w-[520px] z-50 bg-white dark:bg-surface-800 border border-gray-200 dark:border-surface-700 shadow-xl rounded-2xl p-5"
            >
                <div class="text-gray-900 dark:text-white font-semibold mb-1">
                    Usiamo cookie per migliorare l’esperienza
                </div>
                <div class="text-sm text-gray-600 dark:text-gray-300 mb-3">
                    Cookie tecnici sempre attivi. Puoi accettare gli <strong>analitici</strong> (PostHog) per aiutarci a
                    migliorare. Puoi cambiare idea in qualsiasi momento dalle impostazioni privacy.
                </div>
                <div class="flex gap-2 justify-end">
                    <button
                        class="px-4 py-2 rounded-lg border border-gray-300 dark:border-surface-600 text-gray-700 dark:text-gray-200"
                        (click)="rejectAnalytics()"
                    >
                        Rifiuta
                    </button>
                    <button
                        class="px-4 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white"
                        (click)="acceptAnalytics()"
                    >
                        Accetta
                    </button>
                </div>
            </div>
        }
    `
})
export class AppComponent implements OnInit, OnDestroy {
    showCookieBanner: boolean = false;
    analyticsConsent: 'accepted' | 'rejected' | 'unset' = 'unset';

    private changeDetector = inject(ChangeDetectorRef);
    private translate = inject(TranslateService);
    private router = inject(Router);
    private primeng = inject(PrimeNG);
    private analytics = inject(AnalyticsService);
    private seo = inject(SeoService);

    private lastPagePath: string | null = null;

    private readonly document = inject(DOCUMENT);
    private readonly cookieBanner = viewChild<ElementRef<HTMLElement>>('cookieBanner');

    constructor() {
        afterRenderEffect((onCleanup) => {
            const banner = this.cookieBanner()?.nativeElement;
            if (!banner) return;
            const updateOffset = () =>
                this.document.body.style.setProperty(
                    '--compass-workspace-cookie-offset',
                    `${Math.ceil(banner.getBoundingClientRect().height) + 24}px`
                );
            const observer = new ResizeObserver(updateOffset);
            observer.observe(banner);
            updateOffset();
            onCleanup(() => {
                observer.disconnect();
                this.document.body.style.removeProperty('--compass-workspace-cookie-offset');
            });
        });
        this.translate.setTranslation('it', translationsIT);
        this.translate.setDefaultLang('it');
        effect(() => {
            tenantVocabulary();
            // Re-evaluate existing TranslatePipes; the original dictionary remains tenant independent.
            untracked(() => this.translate.setTranslation('it', translationsIT));
        });
        this.translate
            .get('PRIME_NG')
            .pipe(tap(console.log))
            .subscribe((res) => this.primeng.setTranslation(res));

        // Page view tracking
        this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                this.analytics.trackPageView(event.urlAfterRedirects);
                this.applyRouteSeo(event.urlAfterRedirects);
            });

        window.addEventListener('aurora-privacy-settings', this.openPrivacy);

        // Cookie consent state
        const stored = localStorage.getItem('cookieConsent');
        if (stored === 'accepted' || stored === 'rejected') {
            this.analyticsConsent = stored as any;
            this.showCookieBanner = false;
        } else {
            this.showCookieBanner = true;
        }
    }

    ngOnInit(): void {}
    private openPrivacy = () => {
        this.showCookieBanner = true;
        this.changeDetector.markForCheck();
    };
    ngOnDestroy(): void {
        window.removeEventListener('aurora-privacy-settings', this.openPrivacy);
    }

    private applyRouteSeo(url: string): void {
        const path = (url.split('?')[0]?.split('#')[0] || '/').replace(/\/$/, '') || '/';
        const dynamicPrefixes = ['/casi-uso/', '/settori/', '/soluzioni/', '/guide/', '/confronti/'];

        if (path === '/' || path === '/webinar') {
            return;
        }

        if (dynamicPrefixes.some((prefix) => path.startsWith(prefix))) {
            return;
        }

        if (getStaticPageSeo(path)) {
            this.seo.applyForStaticPath(path);
        }
    }

    acceptAnalytics() {
        this.analyticsConsent = 'accepted';
        localStorage.setItem('cookieConsent', 'accepted');
        this.showCookieBanner = false;
        this.analytics.setConsent(true);
    }

    rejectAnalytics() {
        this.analyticsConsent = 'rejected';
        localStorage.setItem('cookieConsent', 'rejected');
        this.showCookieBanner = false;
        this.analytics.setConsent(false);
    }
}
